/**
 * Owner: Masa Bayu
 * File: layout-structure-typing.ts
 * Date:Time: 2026-09-28 10:17
 * Description: STRUCTURE-RECOVERY-02 — typography evidence gates HEADING · OCR bbox ≠ title.
 */

import type { QXDocumentBlock } from './parts';
import type { QXDocumentBBox } from './geometry';
import type { QXDocumentLayout, QXDocumentLayoutBlock, QXDocumentLine } from './layout';

const ORNAMENT_MAX_CHARS = 2;
const HEADING_MAX_CHARS = 200;
const SUBHEADING_MAX_CHARS = 140;
const MIN_STRUCTURE_CHARS = 3;
/** HEADING = clearly above body baseline (not “largest in whole book”). */
const HEADING_OVER_BODY_RATIO = 1.25;
/** Mid bump above body → SUBHEADING when below HEADING floor (typography only). */
const SUBHEADING_OVER_BODY_RATIO = 1.08;
/** Same-size-as-body titles need a clear gap above (layout isolation). */
const ISOLATION_GAP_FACTOR = 3.5;
const ISOLATION_MAX_CHARS = 72;
const SIZE_BUCKET = 0.5;
/** Titles need letter-majority glyphs — not punctuation/OCR symbol soup. */
const MIN_TITLE_LETTERS = 3;
const MIN_TITLE_LETTER_RATIO = 0.55;

function blockText(block: QXDocumentLayoutBlock): string {
  if (block.text && block.text.trim().length > 0) return block.text.trim();
  return block.lines
    .map((line) => line.runs.map((run) => run.text).join(''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * True typography size from Eyes/provider fontSize — typically native PDF text layer.
 * OCR runs usually omit fontSize; do not invent from glyphs.
 */
export function layoutBlockTypographySize(block: QXDocumentLayoutBlock): number | null {
  let best: number | null = null;
  for (const line of block.lines) {
    for (const run of line.runs) {
      if (typeof run.style.fontSize === 'number' && run.style.fontSize > 0) {
        best = best === null ? run.style.fontSize : Math.max(best, run.style.fontSize);
      }
    }
  }
  return best;
}

/** Geometry-only size (bbox height) — weaker; never alone creates HEADING. */
export function layoutBlockGeometrySize(block: QXDocumentLayoutBlock): number | null {
  let best: number | null = null;
  for (const line of block.lines) {
    for (const run of line.runs) {
      if (run.bbox && run.bbox.height > 0) {
        best = best === null ? run.bbox.height : Math.max(best, run.bbox.height);
      }
    }
  }
  if (best !== null) return best;
  if (block.bbox && block.bbox.height > 0) return block.bbox.height;
  return null;
}

/**
 * Prefer typography; fall back to geometry for body-floor aids only.
 * Callers that gate HEADING must use layoutBlockTypographySize instead.
 */
export function layoutBlockVisualSize(block: QXDocumentLayoutBlock): number | null {
  return layoutBlockTypographySize(block) ?? layoutBlockGeometrySize(block);
}

function bucketSize(size: number): number {
  return Math.round(size / SIZE_BUCKET) * SIZE_BUCKET;
}

function isOrnamentText(text: string): boolean {
  const t = text.replace(/\s+/g, '').trim();
  if (t.length === 0) return true;
  if (t.length <= ORNAMENT_MAX_CHARS) return true;
  if (/^[•▪◦●■□‣∙·]+$/u.test(t)) return true;
  return false;
}

/** Generic glyph quality — not a per-title blacklist. */
export function hasStructuralGlyphEvidence(text: string): boolean {
  const compact = text.replace(/\s+/g, '').trim();
  if (compact.length < MIN_STRUCTURE_CHARS) return false;
  let letters = 0;
  for (const ch of compact) {
    if (/\p{L}/u.test(ch)) letters += 1;
  }
  if (letters < MIN_TITLE_LETTERS) return false;
  return letters / compact.length >= MIN_TITLE_LETTER_RATIO;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? null;
}

export type LayoutStructureSizeModel = {
  readonly bodySize: number | null;
  /** Minimum size for HEADING — relative to body, not document max. */
  readonly headingFloor: number | null;
  readonly hasSizedEvidence: boolean;
};

type StyleBucket = {
  size: number;
  count: number;
  charMass: number;
  lengths: number[];
};

/**
 * Body baseline prefers typography sizes; long geometry lines may aid OCR-heavy pages.
 * HEADING floor stays body-relative — not document-max.
 */
export function buildLayoutStructureSizeModel(
  layout: QXDocumentLayout,
): LayoutStructureSizeModel {
  const longSizes: number[] = [];
  const typoBuckets = new Map<number, StyleBucket>();
  const geomBuckets = new Map<number, StyleBucket>();

  const addBucket = (map: Map<number, StyleBucket>, size: number, textLen: number) => {
    const key = bucketSize(size);
    const prev = map.get(key);
    if (prev) {
      prev.count += 1;
      prev.charMass += textLen;
      prev.lengths.push(textLen);
    } else {
      map.set(key, {
        size: key,
        count: 1,
        charMass: textLen,
        lengths: [textLen],
      });
    }
  };

  for (const page of layout.pages) {
    for (const block of page.blocks) {
      if (block.kind !== 'text' && block.kind !== 'other') continue;
      const text = blockText(block);
      if (text.length === 0) continue;
      if (isOrnamentText(text)) continue;

      const typo = layoutBlockTypographySize(block);
      const geom = layoutBlockGeometrySize(block);

      if (typo !== null) {
        if (text.length >= 80) longSizes.push(typo);
        addBucket(typoBuckets, typo, text.length);
      } else if (geom !== null && text.length >= 80) {
        // OCR body flow: geometry may set body floor when native layer is sparse.
        longSizes.push(geom);
        addBucket(geomBuckets, geom, text.length);
      }
    }
  }

  const bodyFromLong = median(longSizes);
  let bodySize: number | null = bodyFromLong;

  const pickBodyFromBuckets = (buckets: Map<number, StyleBucket>): number | null => {
    if (buckets.size === 0) return null;
    const ranked = [...buckets.values()]
      .map((b) => ({
        ...b,
        medianLen: median(b.lengths) ?? 0,
      }))
      .sort((a, b) => b.charMass - a.charMass);
    const flow = ranked.find((b) => b.medianLen >= 40);
    if (flow) return flow.size;
    const repeated = ranked.filter((b) => b.count >= 2).sort((a, b) => a.size - b.size);
    return repeated[0]?.size ?? ranked[ranked.length - 1]?.size ?? null;
  };

  if (bodySize === null) {
    bodySize = pickBodyFromBuckets(typoBuckets) ?? pickBodyFromBuckets(geomBuckets);
  }

  if (bodySize === null) {
    return Object.freeze({
      bodySize: null,
      headingFloor: null,
      hasSizedEvidence: false,
    });
  }

  return Object.freeze({
    bodySize,
    headingFloor: bodySize * HEADING_OVER_BODY_RATIO,
    hasSizedEvidence: true,
  });
}

function titleLike(text: string, maxChars: number): boolean {
  const t = text.trim();
  return (
    t.length >= MIN_STRUCTURE_CHARS &&
    t.length <= maxChars &&
    !isOrnamentText(t) &&
    hasStructuralGlyphEvidence(t)
  );
}

function gapAbove(
  page: { readonly height: number },
  prev: QXDocumentLayoutBlock | null,
  block: QXDocumentLayoutBlock,
): number | null {
  if (!block.bbox) return null;
  // Skip chrome / edge bands as isolation anchors.
  if (prev?.bbox && prev.bbox.y <= page.height * 0.12) {
    prev = null;
  }
  if (!prev?.bbox) {
    // Page-opening short title: near top of page (PDF y grows upward).
    if (block.bbox.y >= page.height * 0.55) {
      return page.height * 0.25;
    }
    return null;
  }
  return Math.abs(prev.bbox.y - block.bbox.y);
}

/**
 * Classify from body-relative size + isolation.
 * HEADING requires typography (fontSize) evidence — OCR bbox height alone is insufficient.
 */
export function classifyLayoutTextBlockType(input: {
  readonly block: QXDocumentLayoutBlock;
  readonly text: string;
  readonly model: LayoutStructureSizeModel;
  readonly page?: { readonly height: number };
  readonly prevBlock?: QXDocumentLayoutBlock | null;
}): QXDocumentBlock['type'] | null {
  const kind = input.block.kind;
  if (kind !== 'text' && kind !== 'other') return null;

  const text = input.text.trim();
  if (text.length < MIN_STRUCTURE_CHARS || isOrnamentText(text)) {
    return 'PARAGRAPH';
  }

  if (!input.model.hasSizedEvidence || input.model.bodySize === null || input.model.headingFloor === null) {
    return 'PARAGRAPH';
  }

  const typo = layoutBlockTypographySize(input.block);
  const geom = layoutBlockGeometrySize(input.block);
  const body = input.model.bodySize;
  const headingFloor = input.model.headingFloor;
  const lines = input.block.lines.length;

  // HEADING: typography only — never promote on OCR/geometry size alone.
  if (
    typo !== null &&
    typo >= headingFloor &&
    titleLike(text, HEADING_MAX_CHARS)
  ) {
    return 'HEADING';
  }

  // Mid SUBHEADING: typography size between body bump and HEADING floor.
  if (
    typo !== null &&
    typo >= body * SUBHEADING_OVER_BODY_RATIO &&
    typo < headingFloor &&
    titleLike(text, SUBHEADING_MAX_CHARS) &&
    lines <= 3
  ) {
    return 'SUBHEADING';
  }

  // Isolation: short titles with whitespace evidence.
  // Typography preferred; geometry-only allowed here only (still not HEADING).
  const sizeForIsolation = typo ?? geom;
  if (
    sizeForIsolation !== null &&
    sizeForIsolation >= body * 0.95 &&
    sizeForIsolation < headingFloor &&
    titleLike(text, ISOLATION_MAX_CHARS) &&
    lines <= 2 &&
    input.page
  ) {
    const gap = gapAbove(input.page, input.prevBlock ?? null, input.block);
    if (gap !== null && gap >= sizeForIsolation * ISOLATION_GAP_FACTOR) {
      return 'SUBHEADING';
    }
  }

  return 'PARAGRAPH';
}

/**
 * Merge consecutive same-page typography HEADING-sized lines into one layout block.
 */
export function mergeConsecutiveHeadingSizedLines(input: {
  readonly layout: QXDocumentLayout;
  readonly model: LayoutStructureSizeModel;
}): QXDocumentLayout {
  if (!input.model.hasSizedEvidence || input.model.headingFloor === null) {
    return input.layout;
  }
  const headingFloor = input.model.headingFloor;
  const pages = input.layout.pages.map((page) => {
    const out: QXDocumentLayoutBlock[] = [];
    let pending: QXDocumentLayoutBlock | null = null;

    const flush = () => {
      if (pending) {
        out.push(pending);
        pending = null;
      }
    };

    const isHeadingSized = (block: QXDocumentLayoutBlock): boolean => {
      if (block.kind !== 'text' && block.kind !== 'other') return false;
      const text = blockText(block);
      if (!titleLike(text, HEADING_MAX_CHARS)) return false;
      const size = layoutBlockTypographySize(block);
      return size !== null && size >= headingFloor;
    };

    for (const block of page.blocks) {
      if (!isHeadingSized(block)) {
        flush();
        out.push(block);
        continue;
      }
      if (!pending) {
        pending = block;
        continue;
      }
      const prevSize = layoutBlockTypographySize(pending);
      const nextSize = layoutBlockTypographySize(block);
      const prevY = pending.bbox?.y ?? null;
      const nextY = block.bbox?.y ?? null;
      const gapOk =
        prevY !== null &&
        nextY !== null &&
        Math.abs(prevY - nextY) <= (prevSize ?? 12) * 2.2;
      const sizeOk =
        prevSize !== null &&
        nextSize !== null &&
        Math.abs(prevSize - nextSize) <= 0.75;

      if (gapOk && sizeOk) {
        const mergedText = `${blockText(pending)} ${blockText(block)}`.replace(/\s+/g, ' ').trim();
        const lines: readonly QXDocumentLine[] = Object.freeze([
          ...pending.lines,
          ...block.lines,
        ]);
        const bbox: QXDocumentBBox | null =
          pending.bbox && block.bbox
            ? Object.freeze({
                x: Math.min(pending.bbox.x, block.bbox.x),
                y: Math.min(pending.bbox.y, block.bbox.y),
                width:
                  Math.max(
                    pending.bbox.x + pending.bbox.width,
                    block.bbox.x + block.bbox.width,
                  ) - Math.min(pending.bbox.x, block.bbox.x),
                height:
                  Math.max(
                    pending.bbox.y + pending.bbox.height,
                    block.bbox.y + block.bbox.height,
                  ) - Math.min(pending.bbox.y, block.bbox.y),
              })
            : pending.bbox;
        pending = Object.freeze({
          ...pending,
          text: mergedText,
          lines,
          bbox,
        });
      } else {
        flush();
        pending = block;
      }
    }
    flush();
    return Object.freeze({ ...page, blocks: Object.freeze(out) });
  });

  return Object.freeze({ pages: Object.freeze(pages) });
}
